import { ChargeMapContext } from './index.model'
import { OS } from '@/utils/device'
import {
  DEFAULT_COUNTRY_ZOOM_LEVEL,
  DEFAULT_DISTRICT_ZOOM_LEVEL,
} from '../../config/zoomLevelConfig'
import {
  ResourceIconMap,
  ResourceIconMapUpgrade,
  ResourceIconSizeMap,
  ResourceIndexMap,
  ResourceSelectedIconMap,
  ResourceSelectedIconMapUpgrade,
} from './mapConfig'
import { Marker, MarkerType, Point, PowerResource, ZoomMode } from '../../contract/map'
import { PowerResourceType } from '@/contract/map.base'
import { gcj02ToBd09 } from '@/utils/coordinateTransform'
import { getPowerResourceTypeV2 } from '../../utils/resource'

/**
 * 移动到指定的点
 * @param {ChargeMapContext} context 地图上下文
 * @param {Point} center 移动到的点
 * @param {number} zoom 缩放等级
 */
export const flyTo = (context: ChargeMapContext, center: Point, zoom?: number) => {
  context.flyToCenterAndZoom(center, zoom)
}

/**
 * 适应电脑屏幕并修改中心点和zoom
 * @param {ChargeMapContext} context 地图上下文
 */
export const fitWindows = (context: ChargeMapContext, points: Point[]) => {
  let leftRight = 10
  const wH = document.documentElement.clientWidth / document.documentElement.clientHeight
  if (document.documentElement.clientWidth > 700 && 0.55 <= wH && wH < 1) {
    // 适配竖屏地图位置不够的情况，如ipad
    leftRight = 100 * wH + 20
  }
  if (window.channel === 'nioVehicleShow') {
    context.adjustCenterAndZoom(points, 10, 10, 10, 150)
  } else {
    // 车机且有数据概览的时候才会偏移
    OS.isNioBrowser && window.NSComponentsConfig?.data_default?.show
      ? context.adjustCenterAndZoom(points, 10, 10, 10, 200)
      : context.adjustCenterAndZoom(points, 10, leftRight, 10, leftRight)
  }
  const viewport = context.getMap().getViewport()
  return { center: viewport.center, zoom: viewport.zoom }
}

/**
 * 改变换电站换电状态
 * @param context
 * @param chargingStatus
 */
export const changeChargingStatus = (context: ChargeMapContext, chargingStatus: any[]) => {
  context.changeChargingStatus(chargingStatus)
}

/**
 * 根据zoom等级计算换电站，充电桩小点size
 * @param currentZoom
 */
export const calcSize = (currentZoom: number) => {
  const minZoom = 5
  const maxZoom = DEFAULT_COUNTRY_ZOOM_LEVEL
  const size = ResourceIconSizeMap[ZoomMode.Country]
  const ratio =
    1.2 + ((currentZoom - minZoom) / (maxZoom - minZoom)) * (window.client === 'mobile' ? 0.8 : 0.8)
  const w = ratio * size.width
  return { w, ratio }
}
// export const calcSizeCountyLayout = (currentZoom: number) => {
//   // 和calcSize统一
//   const minZoom = 5
//   const maxZoom = DEFAULT_COUNTRY_ZOOM_LEVEL
//   const size = ResourceIconSizeMap[ZoomMode.Country]
//   const ratio =
//     1.2 + ((currentZoom - minZoom) / (maxZoom - minZoom)) * (window.client === 'mobile' ? 0.8 : 0.8)
//   const w = ratio * size.width
//   return { w, ratio }
//   // const minZoom = DEFAULT_COUNTRY_ZOOM_LEVEL
//   // const maxZoom = DEFAULT_DISTRICT_ZOOM_LEVEL
//   // const size = 8
//   // const ratio =
//   //   (window.client === 'mobile' ? 0.7 : 1) +
//   //   ((currentZoom - minZoom) / (maxZoom - minZoom)) * (window.client === 'mobile' ? 0.5 : 1.1)
//   // const w = ratio * size
//   // return { w, ratio }
// }

/**
 * 清除资源点周边3km范围圈
 * @param {ChargeMapContext} context 地图上下文
 */
export const removeResourceCircle = (
  context: ChargeMapContext,
  ids?: string[],
  exclude?: boolean
) => {
  context?.removeAllSurroundCircle(ids, exclude)
}

/**
 * 在资源点周边添加3km范围圈
 * @param {ChargeMapContext} context 地图上下文
 * @param {PowerResource[]} resources 加电资源
 */
export const addResourceCircle = (
  context: ChargeMapContext,
  resources: PowerResource[],
  loc?: Point
) => {
  const componentsConfig: any = window.NSComponentsConfig
  if (!componentsConfig?.power_circle?.show) {
    // 电区房圈配置
    return
  }
  for (const resource of resources) {
    const position = resource.location.split(',') // gcj
    const point = gcj02ToBd09({
      lng: Number(position[0]),
      lat: Number(position[1]),
    }) // bd
    if (loc) {
      const dis = context.calculateDistance(
        {
          lng: Number(position[0]),
          lat: Number(position[1]),
        },
        loc
      )
      if (dis <= 3000) {
        context.addSurroundCircle(point, 3000, resource.id)
      }
    } else {
      context.addSurroundCircle(point, 3000, resource.id)
    }
  }
}

/**
 * 画小点
 * @param context
 * @param resources
 * @param key 类型
 * @param id
 */
export const drawPointCountry = async (
  context: ChargeMapContext,
  resources: Point[],
  key: string,
  id?: string,
  display = true
) => {
  if (resources?.length) {
    const currentZoom = context.getZoom()
    switch (key) {
      case 'serviceCenter':
        context.setPointLayer(
          resources,
          (process.config as any).style.dot.serviceCenter,
          calcSize(currentZoom).w,
          id || 'so',
          50,
          display
        )
        break
      case 'testDrive':
        context.setPointLayer(
          resources,
          (process.config as any).style.dot.testDrive,
          calcSize(currentZoom).w,
          id || 'testDrive',
          50,
          display
        )
        break
      case 'nioHouse':
        context.setPointLayer(
          resources,
          (process.config as any).style.dot.nioHouse,
          calcSize(currentZoom).w,
          id || 'nioHouse',
          40,
          display
        )
        break
      case 'nioStore':
        context.setPointLayer(
          resources,
          (process.config as any).style.dot.nioStore,
          calcSize(currentZoom).w,
          id || 'nioStore',
          30,
          display
        )
        break
      case 'swap':
        context.setPointLayer(
          resources,
          (process.config as any).style.dot.swap,
          calcSize(currentZoom).w,
          id || 'swap',
          20,
          display
        )
        break
      case 'charge':
        context.setPointLayer(
          resources,
          (process.config as any).style.dot.charge,
          calcSize(currentZoom).w,
          id || 'charge',
          10,
          display
        )
        break
      case 'third':
        context.setThirdPointLayer(
          resources,
          (process.config as any).style.dot.third,
          calcSize(currentZoom).w,
          id || 'third',
          5,
          display
        )
        break
      default:
        break
    }
  }
}

/**
 * 调整地图加电资源标注的尺寸
 * @param {ChargeMapContext} context 地图上下文
 * @param {PowerResource[]} resources 加电资源
 *
 */
export const resizeMapResources = (context: ChargeMapContext, size: number) => {
  context.resizeAllPointLayer(size)
}

export const fitBound = (list: Point[]) => {
  // 只取最远的sw和ne的点
  let boundsN = list[0].lat
  let boundsS = boundsN
  let boundsW = list[0].lng
  let boundsE = boundsW
  list.forEach((point: Point) => {
    point.lat > boundsN && (boundsN = point.lat)
    point.lat < boundsS && (boundsS = point.lat)
    point.lng > boundsE && (boundsE = point.lng)
    point.lng < boundsW && (boundsW = point.lng)
  })
  const view: Point[] = [
    {
      lng: boundsW,
      lat: boundsS,
    },
    {
      lng: boundsE,
      lat: boundsN,
    },
  ]
  return view
}

/**
 * 点击资源点变大
 * @param {MapContext} context 地图上下文
 * @param {string} newId 新点击的需要变大的ID
 * @param {string} oldId 以前的ID将会还原
 */
export const changeMarker = (context: ChargeMapContext, newId: string, oldId: string) => {
  if (oldId) {
    const oldMarker = context.getMarkerById(oldId)
    if (oldMarker && oldMarker.customData) {
      const markerData = context.getMarkerById(oldId).customData.marker
      context.changeMarker(
        oldId,
        {
          width: 40,
          height: 40,
        },
        markerData.zIndex,
        markerData.icon
      )
    }
  }
  if (newId) {
    const newMarkerData = context.getMarkerById(newId)?.customData?.marker
    if (newMarkerData) {
      const type = getPowerResourceTypeV2(newMarkerData.data)
      const url =
        newMarkerData.data.swapSupportStatus === 2
          ? ResourceSelectedIconMapUpgrade
          : ResourceSelectedIconMap[type]
      context.changeMarker(
        newId,
        {
          width: 60,
          height: 60,
        },
        9000,
        url
      )
    }
  }
}

/**
 * 随zoom改变marker大小
 * @param {MapContext} context 地图上下文
 * @param {number} zoomLevel zoom级别
 */
export const adjustSizeWithZoom = (context: ChargeMapContext, zoomLevel: number) => {
  context.adjustSizeWithZoom(zoomLevel)
}

// 设置随zoom的最小最大宽高
export const minMax = (num: number, min: number, max: number) => {
  // 设置最大最小宽高
  if (num < min) {
    return min
  } else if (num > max) {
    return max
  } else {
    return num
  }
}

/**
 * 通过窗口获取距离
 * @param {MapContext} context 地图上下文
 */
export const getDistanceByViewport = (context: ChargeMapContext) => {
  const center = context?.getCenter() // bd
  const getBounds = context?.getMap()?.getBounds()
  return Math.ceil(context?.calculateDistance(center, getBounds.getSouthWest()))
}

/**
 * 添加全国地图加电资源
 * @param {ChargeMapContext} context 地图上下文
 * @param {ZoomMode} mode 地图模式
 * @param {PowerResource[]} resources 加电资源
 * @param {boolean} useMapVgl 是否使用mapvgl画图， 默认false
 * @param {boolean} zoomable 添加的资源点是否可随zoom放大缩小， 默认false
 */
export const addMapResources = (
  context: ChargeMapContext,
  mode: ZoomMode,
  resources: PowerResource[],
  zoomable = false
) => {
  const config: any = window.NSComponentsConfig
  const serviceCenterMarker: Marker[] = [] // 服务中心
  const testDriveMarker: Marker[] = [] // 试驾点
  const nioSpaceMarker: Marker[] = [] // 蔚来空间
  const nioHouseMarker: Marker[] = [] // 蔚来中心
  const psMarker: Marker[] = []
  const csMarker: Marker[] = []
  const otherMarker: Marker[] = []
  resources.forEach((item) => {
    const type = getPowerResourceTypeV2(item)
    const position = item.location.split(',')
    const point = gcj02ToBd09({
      lng: Number(position[0]),
      lat: Number(position[1]),
    })
    // 换电站动画 画点时就要获取到换电站状态
    const marker = {
      label: ' ', // label: '换电站动画（label不会显示）',
      id: item.id,
      type: MarkerType.Animation,
      loc: point,
      clickable: true,
      zoomable,
      size: ResourceIconSizeMap[mode],
      icon: item.swapSupportStatus === 2 ? ResourceIconMapUpgrade : ResourceIconMap[type],
      zIndex: ResourceIndexMap[type],
      data: item,
      swapStatus: config?.realtime_swap_status?.show && item.swapStatus == 1 ? item.swapStatus : 0, // 是否支持实时换点状态
    }
    switch (type) {
      case PowerResourceType.PowerSwap:
        psMarker.push(marker)
        break
      case PowerResourceType.NioChargeStationNpc:
        csMarker.push(marker)
        break
      case PowerResourceType.NioChargeStationDest:
        csMarker.push(marker)
        break
      case PowerResourceType.NioSpace:
        nioSpaceMarker.push(marker)
        break
      case PowerResourceType.NioHouse:
        nioHouseMarker.push(marker)
        break
      case PowerResourceType.TestDrive:
        testDriveMarker.push(marker)
        break
      case PowerResourceType.ServiceCenter:
        serviceCenterMarker.push(marker)
        break
      default:
        otherMarker.push(marker)
        break
    }
  })
  context.addMarkers(otherMarker)
  context.addMarkers(csMarker)
  context.addMarkers(psMarker)
  context.addMarkers(nioSpaceMarker)
  context.addMarkers(nioHouseMarker)
  context.addMarkers(testDriveMarker)
  context.addMarkers(serviceCenterMarker)
}

/**
 * 绘制心愿地址
 * @param {ChargeMapContext} context 地图上下文
 */
export const drawWishPower = (
  context: ChargeMapContext,
  resources: Point[],
  id: string,
  opt = {}
) => {
  if (resources.length) {
    // 读取配置 颜色,聚合最大最小值
    const config: any = window.NSComponentsConfig
    const options = Object.assign(
      {
        gradient: {
          0: config.wishpower.properties.colors[0],
          0.199999: config.wishpower.properties.colors[0],
          0.2: config.wishpower.properties.colors[1],
          0.399999: config.wishpower.properties.colors[1],
          0.4: config.wishpower.properties.colors[2],
          0.599999: config.wishpower.properties.colors[2],
          0.6: config.wishpower.properties.colors[3],
          0.799999: config.wishpower.properties.colors[3],
          0.8: config.wishpower.properties.colors[4],
          0.999999: config.wishpower.properties.colors[4],
          1: '#FFFFFF',
        },
      },
      opt
    )
    context.setWishPowerLayer(resources, id, options)
  }
}

/**
 * 创建一个带蓝色填充、白色边框的圆形 Canvas 图标
 * @param {number} size - 图标大小（宽高相同）
 * @param {string} fillColor - 填充颜色（如 "#1C7795"）
 * @param {string} strokeColor - 边框颜色（如 "white"）
 * @param {number} strokeWidth - 边框宽度（如 1）
 * @returns {HTMLCanvasElement} - 返回 Canvas 对象
 */
export const createCircleCanvas = (
  size: number,
  fillColor: string,
  strokeColor: string,
  strokeWidth: number
) => {
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')

  // 绘制圆形（填充 + 边框）
  const center = size / 2
  const radius = size / 2 - strokeWidth / 2 // 减去边框宽度的一半，避免溢出

  // 先画填充
  ctx!.beginPath()
  ctx!.arc(center, center, radius, 0, Math.PI * 2)
  ctx!.fillStyle = fillColor
  ctx!.fill()

  // 再画边框
  if (strokeWidth > 0) {
    ctx!.lineWidth = strokeWidth
    ctx!.strokeStyle = strokeColor
    ctx!.stroke()
  }

  return canvas
}

const iconCanvasMap: any = {}
export const getIconCanvas = (color: string, strokeWidth = 10) => {
  if (Object.prototype.hasOwnProperty.call(iconCanvasMap, color)) {
    return iconCanvasMap[color]
  } else {
    const iconCanvas = createCircleCanvas(100, color, 'white', strokeWidth)
    iconCanvasMap[color] = iconCanvas
    return iconCanvas
  }
}
